import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../Css/Blogs.css';
import { API_URL } from '../utils/Function';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

export default function Blogs() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [animatedCards, setAnimatedCards] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const [particles, setParticles] = useState([]);
  const [cornerParticles, setCornerParticles] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const blogsContainerRef = useRef(null);
  const buttonRef = useRef(null);
  const navRef = useRef(null);
  const [hoverId, setHoverId] = useState(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);


  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Dynamic filter links based on categories that have blogs
  const filterLinks = useMemo(() => {
    const staticLinks = [
      { id: 'all', name: 'All', icon: '/images/i.png', width: '90px' }
    ];

    // Get unique category IDs from blogs
    const usedCategoryIds = [...new Set(blogs.map(blog => blog.category_id))];

    // Filter categories to only include those that have blogs
    const categoriesWithBlogs = categories.filter(category =>
      usedCategoryIds.includes(category.id)
    );

    // Map categories to filter links
    const categoryLinks = categoriesWithBlogs.map(category => {
      // Map category names to icons
      const getCategoryIcon = (categoryName) => {
        const iconMap = {
          'Tech News': '/images/Frame.png',
          'Case Studies': '/images/case.png',
          'School Management': '/images/school-management-icon.png',
          'AI Solutions': '/images/ai-solutions-icon.png',
          'Security': '/images/security-icon.png',
          'Cloud Tech': '/images/cloud-icon.png',
          'Analytics': '/images/analytics-icon.png',
          'Mobile Tech': '/images/mobile-icon.png'
        };

        return iconMap[categoryName] || '/images/default-category-icon.png';
      };

      // Calculate width based on category name length
      const calculateWidth = (name) => {
        const baseWidth = 120;
        const charWidth = 8;
        return `${baseWidth + (name.length * charWidth)}px`;
      };

      return {
        id: category.id.toString(),
        name: category.name,
        icon: getCategoryIcon(category.name),
        width: calculateWidth(category.name),
        categoryId: category.id
      };
    });

    return [...staticLinks, ...categoryLinks];
  }, [categories, blogs]);

  // Fetch blogs from backend
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/blog.php/get?status=published`);
      const data = await response.json();

      if (data.success) {
        setBlogs(data.blogs);

        // Extract unique author IDs from blogs
        const authorIds = [...new Set(data.blogs.map(blog => blog.author_id))];
        if (authorIds.length > 0) {
          fetchUsers(authorIds);
        }
      } else {
        console.error('Error fetching blogs:', data.error);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories from backend
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/category.php/get?type=blog`);
      const data = await response.json();

      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch users data based on author IDs
  const fetchUsers = async (authorIds) => {
    try {
      const response = await fetch(`${API_URL}/user.php/get`);
      const data = await response.json();

      if (data.success) {
        const usersMap = {};
        data.users.forEach(user => {
          usersMap[user.id] = user;
        });
        setUsers(usersMap);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    // Fetch initial data
    fetchBlogs();
    fetchCategories();
  }, []);

  // Check scroll position for gradient visibility
  useEffect(() => {
    const checkScroll = () => {
      if (navRef.current && filterLinks.length >= 4) {
        const { scrollLeft, scrollWidth, clientWidth } = navRef.current;
        setShowLeftGradient(scrollLeft > 0);
        setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    const navElement = navRef.current;
    if (navElement && filterLinks.length >= 4) {
      navElement.addEventListener('scroll', checkScroll);
      checkScroll(); // Initial check
    }

    return () => {
      if (navElement) {
        navElement.removeEventListener('scroll', checkScroll);
      }
    };
  }, [filterLinks]);

  // Scroll navigation horizontally
  const scrollNav = (direction) => {
    if (navRef.current && filterLinks.length >= 4) {
      const scrollAmount = 200;
      navRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Function to handle blog click
  const handleBlogClick = (blogSlug) => {
    scrollToTop();
    setTimeout(() => {
      navigate(`/blog/${blogSlug}`);
    }, 100);
  };

  // Map backend blog data to frontend format
  const blogCards = useMemo(() => {
    if (!blogs.length) return [];

    return blogs.map(blog => {
      // Get category name and color
      const category = categories.find(cat => cat.id === blog.category_id) || {};
      const categoryName = category.name || 'Uncategorized';

      // Map category names to colors
      const categoryColors = {
        'Tech News': '#007BFF',
        'Case Studies': '#22C55E',
        'Security': '#EF4444',
        'Cloud Tech': '#A855F7',
        'Analytics': '#F97316',
        'Mobile Tech': '#45B7D1',
        'AI Solutions': '#8B5CF6',
        'School Management': '#06B6D4'
      };

      const categoryColor = categoryColors[categoryName] || '#6B7280';

      // Format date
      const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      };

      // Calculate read time based on content length
      const calculateReadTime = (content) => {
        if (!content) return '2 min read';
        const wordsPerMinute = 200;
        const words = content.split(/\s+/).length;
        const minutes = Math.ceil(words / wordsPerMinute);
        return `${minutes} min read`;
      };

      // Get featured image or first gallery image
      const getBlogImage = (blog) => {
        if (blog.featured_image) {
          return `${API_URL}/uploads/${blog.featured_image}`;
        }

        if (blog.gallery_images) {
          try {
            const gallery = JSON.parse(blog.gallery_images);
            if (gallery.length > 0) {
              return `${API_URL}/uploads/${gallery[0]}`;
            }
          } catch (e) {
            console.error('Error parsing gallery images:', e);
          }
        }

        return '/images/default-blog.png';
      };

      // Get author data from users state
      const author = users[blog.author_id] || {};
      const authorName = author.name || blog.author_name || 'Unknown Author';
      const authorImage = author.image_url
        ? author.image_url
        : author.image
          ? `${API_URL}/uploads/${author.image}`
          : null;

      return {
        id: blog.id,
        title: blog.title,
        excerpt: blog.excerpt || blog.content?.substring(0, 150) + '...' || 'No excerpt available',
        category: categoryName,
        categoryColor: categoryColor,
        date: formatDate(blog.published_at || blog.created_at),
        time: calculateReadTime(blog.content),
        author: {
          name: authorName,
          image: authorImage
        },
        image: getBlogImage(blog),
        author_id: blog.author_id,
        category_id: blog.category_id,
        slug: blog.slug // Make sure slug is included
      };
    });
  }, [blogs, categories, users]);

  // Particle generation functions
  const generateParticles = (count, x, y) => {
    const newParticles = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        id: Math.random(),
        x: x || Math.random() * 100,
        y: y || Math.random() * 100,
        size: Math.random() * 4 + 2,
        duration: Math.random() * 1 + 0.5,
        delay: Math.random() * 0.5
      });
    }
    return newParticles;
  };

  const generateCornerParticles = (count, buttonRect) => {
    if (!buttonRect) return [];

    const newParticles = [];

    for (let i = 0; i < count; i++) {
      const startX = buttonRect.right - 8;
      const startY = buttonRect.bottom - 8;

      const angle = Math.random() * Math.PI / 3 + Math.PI / 6;
      const distance = 80 + Math.random() * 200;

      const endX = startX + Math.cos(angle) * distance;
      const endY = startY + Math.sin(angle) * distance;

      const midX1 = startX + Math.cos(angle) * distance * 0.2;
      const midY1 = startY + Math.sin(angle) * distance * 0.2;
      const midX2 = startX + Math.cos(angle) * distance * 0.5;
      const midY2 = startY + Math.sin(angle) * distance * 0.5;
      const midX3 = startX + Math.cos(angle) * distance * 0.8;
      const midY3 = startY + Math.sin(angle) * distance * 0.8;

      const speedTypes = ['slow', 'medium', 'fast'];
      const speed = speedTypes[Math.floor(Math.random() * speedTypes.length)];

      const shapeTypes = ['circle', 'blob', 'drop'];
      const shape = shapeTypes[Math.floor(Math.random() * shapeTypes.length)];

      const baseColors = [
        'rgba(72, 52, 212, 0.9)',
        'rgba(104, 109, 224, 0.8)',
        'rgba(91, 33, 182, 0.7)',
      ];
      const color = baseColors[Math.floor(Math.random() * baseColors.length)];

      newParticles.push({
        id: Math.random(),
        startX: `${startX}px`,
        startY: `${startY}px`,
        endX: `${endX}px`,
        endY: `${endY}px`,
        midX1: `${midX1}px`,
        midY1: `${midY1}px`,
        midX2: `${midX2}px`,
        midY2: `${midY2}px`,
        midX3: `${midX3}px`,
        midY3: `${midY3}px`,
        size: Math.random() * 5 + 2,
        color: color,
        speed: speed,
        shape: shape,
        opacity: Math.random() * 0.7 + 0.3,
        createdAt: Date.now()
      });
    }
    return newParticles;
  };

  const handleMouseEnter = (e) => {
    setIsHovered(true);
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setParticles(generateParticles(8, 50, 50));
      setCornerParticles(generateCornerParticles(6, buttonRect));
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setTimeout(() => {
      setParticles([]);
      setTimeout(() => setCornerParticles([]), 1000);
    }, 500);
  };

  useEffect(() => {
    const container = blogsContainerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedCards(blogCards.map((card) => card.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    if (container) observer.observe(container);

    return () => {
      if (container) observer.unobserve(container);
    };
  }, [blogCards]);

  useEffect(() => {
    if (isHovered && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();

      const particleInterval = setInterval(() => {
        setParticles(prev => [...prev, ...generateParticles(2)]);
      }, 200);

      const cornerParticleInterval = setInterval(() => {
        setCornerParticles(prev => {
          const updatedParticles = prev.filter(p =>
            Date.now() - p.createdAt < 4000
          );

          const newParticles = generateCornerParticles(2, buttonRect).map(p => ({
            ...p,
            createdAt: Date.now()
          }));

          return [...updatedParticles, ...newParticles];
        });
      }, 300);

      return () => {
        clearInterval(particleInterval);
        clearInterval(cornerParticleInterval);
      };
    }
  }, [isHovered]);

  // Update particle positions on scroll/resize
  useEffect(() => {
    const updateParticles = () => {
      if (isHovered && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        setCornerParticles(prev =>
          prev.map(particle => ({
            ...particle,
            startX: `${buttonRect.right - 8}px`,
            startY: `${buttonRect.bottom - 8}px`
          }))
        );
      }
    };

    window.addEventListener('scroll', updateParticles);
    window.addEventListener('resize', updateParticles);

    return () => {
      window.removeEventListener('scroll', updateParticles);
      window.removeEventListener('resize', updateParticles);
    };
  }, [isHovered]);

  // Filter blogs based on active filter
  const filteredBlogs = useMemo(() => {
    if (activeFilter === 'all') {
      return blogCards;
    }

    // For category filters, use the category ID from filterLinks
    const activeFilterLink = filterLinks.find(link => link.id === activeFilter);
    if (activeFilterLink && activeFilterLink.categoryId) {
      return blogCards.filter(blog => blog.category_id === activeFilterLink.categoryId);
    }

    return blogCards;
  }, [blogCards, activeFilter, filterLinks]);

  // Handle view all blogs navigation
  const handleViewAllBlogs = () => {
    scrollToTop();
    setTimeout(() => {
      navigate('/blogs');
    }, 100);
  };

  return (
    <div className="blogs-container" ref={blogsContainerRef}>
      {/* Bottom Corner Leak Particles */}
      {cornerParticles.map(particle => (
        <div
          key={particle.id}
          className={`bottom-corner-leak-particle bottom-corner-leak-particle--${particle.speed} bottom-corner-leak-particle--${particle.shape}`}
          style={{
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            background: particle.color,
            left: 0,
            top: 0,
            '--start-x': particle.startX,
            '--start-y': particle.startY,
            '--mid-x1': particle.midX1,
            '--mid-y1': particle.midY1,
            '--mid-x2': particle.midX2,
            '--mid-y2': particle.midY2,
            '--mid-x3': particle.midX3,
            '--mid-y3': particle.midY3,
            '--end-x': particle.endX,
            '--end-y': particle.endY,
            opacity: particle.opacity
          }}
        />
      ))}

      <img src="/images/top-right-cornrer.png" alt="" className="top-right-corner" />

      <div className="heading-section">
        <h2 className="main-heading">
          <span className="bulb-icon">
            <img src="/images/Bulp.png" alt="" />
          </span>
          Latest Insights & Updates
        </h2>
        <p className="sub-heading">
          Explore technology trends, product updates, and expert insights from SafPro.
        </p>

        <div className="blogs-nav-wrapper">
          {filterLinks.length >= 4 && showLeftGradient && (
            <div className="nav-gradient nav-gradient-left"></div>
          )}

          <div
            ref={navRef}
            className={`filter-links scrollable-nav ${filterLinks.length >= 4 ? 'more-than-four' : ''}`}
            style={{
              display: "flex",
              justifyContent: filterLinks.length >= 4 ? "flex-start" : "center",
              gap: "12px",
              marginTop: "20px",
              flexWrap: "nowrap",
              overflowX: "auto",
              padding: "10px 0",
              width: "100%",
              maxWidth: "1000px"
            }}
          >
            {filterLinks.map((link) => {
              const isActive = activeFilter === link.id;
              const isHover = hoverId === link.id;

              return (
                <button
                  key={link.id}
                  className="filter-link"
                  onMouseEnter={() => setHoverId(link.id)}
                  onMouseLeave={() => setHoverId(null)}
                  onClick={() => setActiveFilter(link.id)}
                  style={{
                    height: "50px",
                    borderRadius: "9999px",
                    border: "1px solid",
                    borderColor: isActive || isHover ? "#4d3dd6" : "#E5E7EB",
                    background: isActive || isHover ? "#4d3dd6" : "#FFFFFF",
                    color: isActive || isHover ? "#FFFFFF" : "#374151",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    padding: "0 20px",
                    minWidth: "fit-content",
                    width: link.width
                  }}
                >
                  <span style={{ color: isActive || isHover ? "#fff" : "#374151" }}>
                    {link.name}
                  </span>
                </button>
              );
            })}
          </div>

          {filterLinks.length >= 4 && showRightGradient && (
            <div className="nav-gradient nav-gradient-right"></div>
          )}

          {/* Scroll buttons only when more than 4 categories */}
          {filterLinks.length >= 4 && (
            <>
              {showLeftGradient && (
                <button
                  className="nav-scroll-button nav-scroll-left"
                  onClick={() => scrollNav('left')}
                  aria-label="Scroll left"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
              )}
              {showRightGradient && (
                <button
                  className="nav-scroll-button nav-scroll-right"
                  onClick={() => scrollNav('right')}
                  aria-label="Scroll right"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              )}
            </>
          )}
        </div>

      </div>

      {/* Blog Cards Grid */}
      <div className="blog-cards-grid">
        {loading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="blog-card loading">
              <div className="card-image loading-skeleton"></div>
              <div className="card-content">
                <div className="loading-skeleton title-skeleton"></div>
                <div className="loading-skeleton excerpt-skeleton"></div>
                <div className="card-footer">
                  <div className="author-info">
                    <div className="author-image loading-skeleton"></div>
                    <div className="author-details">
                      <div className="loading-skeleton name-skeleton"></div>
                      <div className="loading-skeleton date-skeleton"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : filteredBlogs.length > 0 ? (
          filteredBlogs.map((card) => (
            <div
              key={card.id}
              className={`blog-card ${animatedCards.includes(card.id) ? 'animate' : ''}`}
            >
              <div className="card-images">
                <img src={card.image} alt={card.title} />
                <div
                  className="image-category-badge"
                  style={{ backgroundColor: card.categoryColor }}
                >
                  {card.category}
                </div>
              </div>
              <div className="card-content">
                <h3 className="card-title">{card.title}</h3>
                <p className="card-excerpt">{card.excerpt}</p>
                <div className="card-footer">
                  <div className="author-info">
                    {card.author.image ? (
                      <img
                        src={`${API_URL}/${card.author.image}`}
                        alt={card.author.name}
                        className="author-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div className="author-icon">
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                    )}
                    {!card.author.image && (
                      <div className="author-icon" style={{ display: 'flex' }}>
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                    )}
                    <div className="author-details">
                      <span className="author-name">{card.author.name}</span>
                      <span className="post-date">
                        {card.date} • {card.time}
                      </span>
                    </div>
                  </div>
                  <button
                    className="read-more-btn"
                    onClick={() => handleBlogClick(card.slug)}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.16675 10H15.8334"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10.8334 5L15.8334 10L10.8334 15"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-blogs-message">
            <p>No blogs found for this category. Check back later for new insights!</p>
          </div>
        )}
      </div>

      {/* View All Blogs Button */}
      <div style={{ textAlign: 'center', marginTop: '60px', marginBottom: '40px' }}>
        <button
          ref={buttonRef}
          className={`modern-btn ${isHovered ? 'modern-btn--hovered' : ''}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleViewAllBlogs}
          style={{
            fontFamily: 'Orbitron, sans-serif',
            fontWeight: 700,
            fontSize: '11px',
            lineHeight: '100%',
            letterSpacing: '10%',
            textTransform: 'uppercase'
          }}
        >
          <span className="modern-btn__text">View All Blogs ↗</span>

          <span className="modern-btn__border modern-btn__border--top"></span>
          <span className="modern-btn__border modern-btn__border--right"></span>
          <span className="modern-btn__border modern-btn__border--bottom"></span>
          <span className="modern-btn__border modern-btn__border--left"></span>

          <div className="modern-btn__particles">
            {particles.map(particle => (
              <span
                key={particle.id}
                className="modern-btn__particle"
                style={{
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  animationDuration: `${particle.duration}s`,
                  animationDelay: `${particle.delay}s`
                }}
              />
            ))}
          </div>

          <span className="modern-btn__glow"></span>
        </button>
      </div>
    </div>
  );
}